import { createRouter, createWebHistory } from 'vue-router'
import LandingView from '../views/LandingView.vue'
import LoginView from '../views/LoginView.vue'
import FullLayout from '@/layouts/FullLayout.vue'
import { useAuthStore } from '@/stores/auth'
import { getAuth } from '../utils/authStorage'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/event-testing', name: 'event-testing',
      component: () => import('../views/EventTestingView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ['Admin'] }
    },
    {
      path: '/',
      name: 'landing',
      component: LandingView
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/ProfileView.vue'),
      meta: { requiresAuth: true, layout: FullLayout }
    },
    {
      path: '/chat',
      name: 'chat',
      component: () => import('../views/ChatView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Test", "Client", "Admin"] }
    },
    {
      path: '/search',
      name: 'search',
      component: () => import('../views/SearchView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Client", "Admin"] }
    },
    {
      path: '/traces/:id',
      name: 'trace',
      component: () => import('../views/TraceView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      path: '/observability/traces',
      name: 'observability-traces',
      component: () => import('../views/TracesView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/UsersView.vue'),
      meta: { requiresAuth: true, layout: FullLayout }
    },
    {
      path: '/workflow',
      name: 'workflow',
      component: () => import('../views/WorkflowView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      path: '/models',
      name: 'models',
      component: () => import('../views/ModelsView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      path: '/package-limits',
      name: 'package-limits',
      redirect: { path: '/models', query: { tab: 'limits' } },
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      path: '/skills',
      name: 'skills',
      component: () => import('../views/SkillsView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      // FRMD-169. The backend enforces the admin role on every endpoint as well.
      path: '/prepared-questions',
      name: 'prepared-questions',
      component: () => import('../views/PreparedQuestionsView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      path: '/rag-documents',
      name: 'rag-documents',
      component: () => import('../views/RagDocumentsView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      path: '/widget-renderer',
      name: 'widget-renderer',
      component: () => import('../views/WidgetRendererView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin"] }
    },
    {
      // Personal document store — every signed-in role manages only its own documents.
      path: '/my-documents',
      name: 'my-documents',
      component: () => import('../views/MyDocumentsView.vue'),
      meta: { requiresAuth: true, layout: FullLayout, role: ["Admin", "Client", "Test"] }
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ],
})

router.beforeEach(async (to) => {
  const authStore = useAuthStore()
  const userAuth = getAuth()

  // If user is authenticated and visiting landing or login page, redirect to chat
  if ((to.path === '/' || to.path === '/login') && userAuth?.token) {
    return '/chat'
  }

  // If route requires auth and no token, redirect to login
  if (to.meta.requiresAuth && !userAuth?.token) {
    return '/login'
  }

  // If we have token but store is not initialized, validate and update store
  if (userAuth?.token && !authStore.isAuthenticated) {
    const isValid = await authStore.validateToken()
    if (!isValid) {
      return '/login'
    }
  }

  // Check role-based access
  if (to.meta.role) {
    const roles = to.meta.role as string[]
    const userRole = userAuth?.user?.role

    if (userRole === 'admin' && roles.includes('Admin')) {
      return true
    }

    if (userRole === 'client' && roles.includes('Client')) {
      return true
    }

    if (userRole === 'test' && roles.includes('Test')) {
      return true
    }

    return '/chat'
  }
})

export default router
